/**
 * Content Pages Service
 *
 * Handles API calls for CMS-managed content pages.
 * Used when REACT_APP_USE_CMS_CONTENT is enabled.
 *
 * API Documentation: docs/cms_content/docs/01_ecommerce_frontend_api_guide.md
 */

import api from './api';

const contentService = {
  /**
   * Get a content page by slug
   *
   * @param {string} slug - Page slug (e.g., 'contact', '2026-fifa-world-cup')
   * @returns {Promise<object>} - API response with page data
   *
   * @example
   * const result = await contentService.getPageBySlug('contact');
   * if (result.success) {
   *   console.log(result.data.title);
   * }
   */
  async getPageBySlug(slug) {
    try {
      const response = await api.get(`/pages/slug/${slug}`);

      return {
        success: true,
        data: response.data.data,
        timestamp: response.data.timestamp
      };
    } catch (error) {
      console.error(`[contentService] Error fetching page "${slug}":`, error);

      // Handle 404 specifically
      if (error.status === 404) {
        return {
          success: false,
          error: 'Page not found',
          slug
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to fetch page',
        slug
      };
    }
  },

  /**
   * Get all published content pages
   *
   * @returns {Promise<object>} - API response with list of pages
   *
   * @example
   * const result = await contentService.getAllPages();
   * if (result.success) {
   *   result.data.forEach(page => console.log(page.title));
   * }
   */
  async getAllPages() {
    try {
      const response = await api.get('/pages');

      return {
        success: true,
        data: response.data.data || [],
        timestamp: response.data.timestamp
      };
    } catch (error) {
      console.error('[contentService] Error fetching all pages:', error);

      return {
        success: false,
        error: error.message || 'Failed to fetch pages',
        data: []
      };
    }
  },

  /**
   * Get contact page with parsed JSON content
   *
   * The contact page content is stored as JSON string containing:
   * - description: Intro text for the contact section
   * - locations: Array of location objects with title, mobile, email, address
   *
   * @returns {Promise<object>} - Contact page data with parsed locations
   *
   * @example
   * const result = await contentService.getContactPage();
   * if (result.success && result.data.parsedContent) {
   *   console.log(result.data.parsedContent.description);
   *   result.data.parsedContent.locations.forEach(loc => {
   *     console.log(loc.title, loc.mobile);
   *   });
   * }
   */
  async getContactPage() {
    const result = await this.getPageBySlug('contact');

    if (result.success && result.data?.content) {
      try {
        result.data.parsedContent = JSON.parse(result.data.content);
      } catch (e) {
        console.error('[contentService] Failed to parse contact content as JSON:', e);
        console.warn('[contentService] Contact content is not valid JSON, returning raw content');
      }
    }

    return result;
  },

  /**
   * Get product listing section header
   *
   * Fetches a content page and returns it formatted as a section header
   * with title and description fields.
   *
   * @param {string} slug - Section slug (e.g., '2026-fifa-world-cup')
   * @returns {Promise<object>} - Section header data
   *
   * @example
   * const result = await contentService.getSectionHeader('2026-fifa-world-cup');
   * if (result.success) {
   *   console.log(result.data.title);       // "2026 FIFA World Cup"
   *   console.log(result.data.description); // "The FIFA World Cup is..."
   * }
   */
  async getSectionHeader(slug) {
    const result = await this.getPageBySlug(slug);

    if (result.success && result.data) {
      return {
        success: true,
        data: {
          title: result.data.title,
          description: result.data.content,
          metaTitle: result.data.metaTitle,
          metaDescription: result.data.metaDescription
        }
      };
    }

    return result;
  },

  /**
   * Check if a page exists (without fetching full content)
   *
   * @param {string} slug - Page slug to check
   * @returns {Promise<boolean>} - True if page exists and is published
   */
  async pageExists(slug) {
    try {
      const response = await api.get(`/pages/slug/${slug}/exists`);
      return response.data.data === true;
    } catch (error) {
      return false;
    }
  },

  /**
   * Get hero carousel slides for a specific page
   *
   * @param {string} page - Page identifier (default: 'home')
   * @returns {Promise<object>} - Hero slides data
   *
   * @example
   * const result = await contentService.getHeroSlides('home');
   * if (result.success) {
   *   result.slides.forEach(slide => console.log(slide.title));
   * }
   */
  async getHeroSlides(page = 'home') {
    try {
      // Backend expects path parameter: /content/hero-slides/{page}
      const response = await api.get(`/content/hero-slides/${page}`);

      // Backend returns { slides: [...] } directly
      return {
        success: true,
        slides: response.data.slides || [],
        timestamp: response.data.timestamp
      };
    } catch (error) {
      console.error('[contentService] Error fetching hero slides:', error);

      return {
        success: false,
        slides: [],
        error: error.message || 'Failed to fetch hero slides'
      };
    }
  },

  /**
   * Get page background with banner content
   *
   * @param {string} page - Page identifier ('home', 'product-listing')
   * @returns {Promise<object>} - Background data with banner content
   *
   * @example
   * const result = await contentService.getPageBackground('home');
   * if (result.success) {
   *   console.log(result.imageUrl);
   *   console.log(result.title); // Banner title
   *   console.log(result.buttonText); // Button text
   * }
   */
  async getPageBackground(page) {
    try {
      const response = await api.get(`/content/page-backgrounds/${page}`);

      return {
        success: true,
        page: response.data.page,
        imageUrl: response.data.imageUrl,
        // Banner content fields
        title: response.data.title,
        subtitle: response.data.subtitle,
        buttonText: response.data.buttonText,
        buttonLink: response.data.buttonLink,
        buttonStyle: response.data.buttonStyle,
        readMoreContent: response.data.readMoreContent,
        updatedAt: response.data.updatedAt
      };
    } catch (error) {
      console.error(`[contentService] Error fetching background for "${page}":`, error);

      return {
        success: false,
        page,
        imageUrl: null,
        title: null,
        subtitle: null,
        buttonText: null,
        buttonLink: null,
        buttonStyle: null,
        readMoreContent: null,
        error: error.message || 'Failed to fetch background'
      };
    }
  }
};

export default contentService;
